/**
 * PatientMassageIntake — SOAP-style massage intake (Part 1, Subjective).
 * Iter 72 — replaces the prior AMTA medical-history form.
 *
 * What the client tells the therapist before the first appointment:
 *   • Reason for the visit + changes since last session
 *   • Pain Scale (0-10) and pain areas
 *   • Client goals / focus
 *   • Pressure preference & areas to avoid
 *   • Brief safety screen (pregnancy, blood thinners, surgery, allergies, meds)
 *   • Country-specific consent + signature
 *
 * Wired to:
 *   GET  /api/massage/intake/me
 *   POST /api/massage/intake     (patient self-submit, subjective only)
 *   GET  /api/massage/policies   (country-specific consent text)
 *
 * Mounted at /patient/massage-intake.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { HeartHandshake, Save, Sparkles, Loader2, ShieldCheck, AlertCircle, FileSignature } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../../components/SignaturePad';
import useAutosave, { AutosaveIndicator } from '../../hooks/useAutosave';

const EMPTY = {
  reason_for_visit: '',
  pain_intensity: 0,
  pain_areas: [],
  client_goals: '',
  pressure_preference: '',
  areas_to_avoid: [],
  changes_since_last_session: '',
  pregnant: null,
  blood_thinners: null,
  recent_surgery: null,
  allergies: '',
  medications: '',
  other_conditions: '',
  consents_to_treatment: false,
  consents_to_data_processing: false,
  understands_no_diagnosis: false,
  accepts_draping_policy: false,
  signature: '',
  signed_at: '',
};

const BODY_AREAS = ['head', 'face', 'neck', 'shoulders', 'upper-back', 'mid-back', 'lower-back', 'chest', 'arms', 'forearms', 'hands', 'hips', 'glutes', 'thighs', 'knees', 'calves', 'feet'];

export default function PatientMassageIntake() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);

  const { lastSavedAt, restore, clear } = useAutosave(`massage-intake:${user?.user_id || 'me'}`, form);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get('/massage/intake/me'),
          api.get('/massage/policies'),
        ]);
        const draft = restore();
        const sub = (r1.data && r1.data.subjective) || {};
        // Coalesce null values to keep React happy on controlled inputs.
        const serverClean = Object.fromEntries(
          Object.entries(sub).map(([k, v]) => [k, v == null ? (typeof EMPTY[k] === 'boolean' ? null : EMPTY[k]) : v])
        );
        setForm({ ...EMPTY, ...serverClean, ...(draft || {}) });
        setPolicy(r2.data);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm(f => ({
    ...f, [k]: (f[k] || []).includes(v) ? f[k].filter(x => x !== v) : [...(f[k] || []), v],
  }));

  const consentsOk = form.consents_to_treatment && form.consents_to_data_processing && form.understands_no_diagnosis && form.accepts_draping_policy;
  const canSave = !!form.reason_for_visit && consentsOk && !!form.signature;

  const save = async () => {
    if (!form.reason_for_visit?.trim()) {
      toast.error('Please describe what is bringing you in today.');
      return;
    }
    if (!consentsOk) {
      toast.error('All 4 consent checkboxes are required before saving.');
      return;
    }
    if (!form.signature) {
      toast.error('Please sign to confirm.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/massage/intake', form);
      clear();
      toast.success('Massage intake saved. You can book your appointment now.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">MASSAGE THERAPY · SUBJECTIVE</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="mi-title">Massage intake — Part 1</h1>
          <p className="text-fg-muted text-sm mt-1">
            Fill this in once before your first session, and update it briefly before each follow-up so your therapist
            knows what's changed. Your therapist completes Part 2 (objective findings + plan) after the session.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AutosaveIndicator lastSavedAt={lastSavedAt} onClear={clear}/>
          <button onClick={save}
                  disabled={saving || !canSave}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="mi-save"
                  title={!form.reason_for_visit ? 'Tell us what brings you in' : !consentsOk ? 'Tick all 4 consent boxes first' : !form.signature ? 'Sign to confirm before saving' : ''}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            {saving ? 'Saving…' : 'Save intake'}
          </button>
        </div>
      </div>

      {/* SUBJECTIVE — patient narrative + pain */}
      <Card icon={Sparkles} title="S — Subjective (your story)">
        <Field label="What brings you in today? (reason for the visit)">
          <textarea className="input min-h-[80px]" value={form.reason_for_visit}
                    onChange={(e) => set('reason_for_visit', e.target.value)}
                    placeholder="e.g. tight upper back after long-haul flights, tension headaches…"
                    data-testid="mi-reason"/>
        </Field>
        <Field label="Changes since last session (skip if this is your first visit)">
          <textarea className="input min-h-[60px]" value={form.changes_since_last_session}
                    onChange={(e) => set('changes_since_last_session', e.target.value)}
                    placeholder="e.g. neck mobility better, low back stiffness returned after lifting…"
                    data-testid="mi-changes"/>
        </Field>
        <Field label="Current pain (0 = none, 10 = worst)">
          <Slider value={form.pain_intensity || 0} onChange={(v) => set('pain_intensity', v)} testid="mi-pain"/>
        </Field>
        <Field label="Where is the pain or discomfort? (tap to select)">
          <ChipRow options={BODY_AREAS} selected={form.pain_areas} onToggle={(v) => toggleArr('pain_areas', v)} testid="mi-pain-areas"/>
        </Field>
        <Field label="Your goals / what you want from this session">
          <textarea className="input min-h-[60px]" value={form.client_goals}
                    onChange={(e) => set('client_goals', e.target.value)}
                    placeholder="e.g. release neck tension, relaxation, sleep better tonight"
                    data-testid="mi-goals"/>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Preferred pressure">
            <select className="input" value={form.pressure_preference || ''}
                    onChange={(e) => set('pressure_preference', e.target.value)}
                    data-testid="mi-pressure">
              <option value="">— Select —</option>
              <option value="light">Light</option>
              <option value="medium">Medium</option>
              <option value="firm">Firm</option>
              <option value="deep">Deep</option>
            </select>
          </Field>
          <Field label="Areas to avoid (tap to select)">
            <ChipRow options={BODY_AREAS} selected={form.areas_to_avoid} onToggle={(v) => toggleArr('areas_to_avoid', v)} testid="mi-avoid"/>
          </Field>
        </div>
      </Card>

      {/* Safety screen — short version (the comprehensive medical history lives
          in the standard patient interview shared across disciplines). */}
      <Card icon={AlertCircle} title="Brief safety screen" tint="amber">
        <p className="text-xs text-fg-muted">
          Just a quick check so your therapist can keep you safe. If you've completed the full patient interview already,
          you can skip anything you've answered there.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <YesNo label="Pregnant?" value={form.pregnant} onChange={(v) => set('pregnant', v)} testid="mi-pregnant"/>
          <YesNo label="On blood thinners?" value={form.blood_thinners} onChange={(v) => set('blood_thinners', v)} testid="mi-thinners"/>
          <YesNo label="Recent surgery (< 6 months)?" value={form.recent_surgery} onChange={(v) => set('recent_surgery', v)} testid="mi-surgery"/>
        </div>
        <Field label="Allergies (e.g. essential oils, latex, nuts)">
          <input className="input" value={form.allergies} onChange={(e) => set('allergies', e.target.value)} placeholder="lavender oil, latex, …" data-testid="mi-allergies"/>
        </Field>
        <Field label="Current medications">
          <textarea className="input min-h-[60px]" value={form.medications} onChange={(e) => set('medications', e.target.value)} data-testid="mi-meds"/>
        </Field>
        <Field label="Anything else the therapist should know?">
          <textarea className="input min-h-[60px]" value={form.other_conditions} onChange={(e) => set('other_conditions', e.target.value)} data-testid="mi-other"/>
        </Field>
      </Card>

      {/* Country-specific policy & consent */}
      {policy && (
        <Card icon={ShieldCheck} title={`Consent & policy (${policy.country_label || policy.country})`} tint="blue">
          <div className="space-y-3 text-xs text-fg-secondary mb-4">
            <PolicyClause label="Scope of practice" text={policy.scope_of_practice}/>
            <PolicyClause label="Draping policy"   text={policy.draping_policy}/>
            <PolicyClause label="Data processing"  text={policy.gdpr_clause}/>
            <PolicyClause label="Complaints"       text={policy.complaints_procedure}/>
            {policy.minor_consent && <PolicyClause label="Minors" text={policy.minor_consent}/>}
          </div>
          <div className="space-y-2">
            <Consent label="I consent to massage therapy treatment." value={form.consents_to_treatment} onChange={(v) => set('consents_to_treatment', v)} testid="mi-consent-treatment"/>
            <Consent label="I consent to processing of my health data per the data-processing clause above." value={form.consents_to_data_processing} onChange={(v) => set('consents_to_data_processing', v)} testid="mi-consent-data"/>
            <Consent label="I understand the therapist will not diagnose, treat or prescribe for medical conditions." value={form.understands_no_diagnosis} onChange={(v) => set('understands_no_diagnosis', v)} testid="mi-consent-nodx"/>
            <Consent label="I have read and accept the draping policy." value={form.accepts_draping_policy} onChange={(v) => set('accepts_draping_policy', v)} testid="mi-consent-draping"/>
          </div>
          <div className="mt-4">
            <div className="text-xs text-fg-muted mb-1 flex items-center gap-1.5">
              <FileSignature className="w-3.5 h-3.5"/> Signature
            </div>
            <SignaturePad value={form.signature} onChange={(sig) => set('signature', sig)} testid="mi-signature"/>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---- Small primitives ----
function Card({ icon: Icon, title, children, tint = 'default' }) {
  const tints = {
    default: 'border-soft bg-bg-primary',
    amber:   'border-[#FFB000]/50 bg-[#FFF9EC]',
    blue:    'border-[#003D80]/40 bg-[#F0F5FA]',
  };
  return (
    <div className={`rounded-lg border p-5 mb-4 space-y-3 ${tints[tint]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-brand"/>
        <h3 className="font-heading font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      <div className="flex gap-2" data-testid={testid}>
        {[['Yes', true], ['No', false]].map(([lbl, v]) => (
          <button key={lbl} type="button" onClick={() => onChange(v)}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs border transition ${value === v ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand'}`}>
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipRow({ options, selected = [], onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(o => {
        const on = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)}
                  className={`text-[11px] px-2 py-1 rounded-full border transition ${on ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand'}`}>
            {o.replace('-', ' ')}
          </button>
        );
      })}
    </div>
  );
}

function Slider({ value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-fg-muted mb-1">
        <span>0</span>
        <span className="font-bold text-brand text-base">{value}/10</span>
        <span>10</span>
      </div>
      <input type="range" min="0" max="10" value={value} onChange={(e) => onChange(parseInt(e.target.value))}
             className="w-full accent-brand" data-testid={testid}/>
    </div>
  );
}

function Consent({ label, value, onChange, testid }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer" data-testid={testid}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-brand mt-0.5"/>
      <span className="text-xs">{label}</span>
    </label>
  );
}

function PolicyClause({ label, text }) {
  if (!text) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold">{label}</div>
      <div className="text-xs leading-relaxed">{text}</div>
    </div>
  );
}
